import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { ChatLogEntryView } from "@/api/types";
import { formatSlotRange, formatTimeOfDay } from "@/lib/datetime";
import { cn } from "@/lib/utils";

import { buildTimelineModel, DayTimeline, Interval } from "./timelineModel";

const DAY_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
});

/**
 * Visualises where the agents' offered availability ranges overlap. Availability
 * is shown as one card PER DAY (a horizontal carousel), each with a lane of bars
 * per participant on that day's time axis, the shared band highlighted, and the
 * booked slot marked — so it's obvious which days AND times overlap, not just
 * that a single time was found.
 *
 * <p>Derived entirely from the conversation's chat-log rows (each agent's latest
 * propose/counter + any accept), so it stays in sync with the drawer's re-fetch.</p>
 */
export function OverlapTimeline({ rows }: { rows: ChatLogEntryView[] }) {
  const model = React.useMemo(() => buildTimelineModel(rows), [rows]);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Scroll the carousel by roughly one card. Wheel/trackpad also work, but the
  // drawer's vertical scroll area hides the horizontal scrollbar, so buttons are
  // the reliable affordance.
  const scrollByCards = (dir: 1 | -1) =>
    scrollRef.current?.scrollBy({ left: dir * 232, behavior: "smooth" });

  // Only worth showing once at least two parties (or a booking) are on the axis.
  if (!model || (model.lanes.length < 2 && !model.booked)) return null;

  const multiDay = model.days.length > 1;

  return (
    <div className="mb-3 rounded-md border border-border bg-card p-2">
      <div className="flex items-center justify-between">
        <div className="font-comic-headline text-xs uppercase tracking-wide text-muted-foreground">
          {`Availability overlap${multiDay ? ` · ${model.days.length} days` : ""}`}
        </div>
        {multiDay && (
          <div className="flex gap-1.5">
            <ScrollButton dir={-1} onClick={() => scrollByCards(-1)} />
            <ScrollButton dir={1} onClick={() => scrollByCards(1)} />
          </div>
        )}
      </div>

      {/*
        Cap the scroller to the drawer's inner width. The drawer's Radix
        ScrollArea viewport is shrink-to-fit (display:table), so an uncapped
        overflow-x row would stretch the whole card past the panel and push the
        header buttons off-screen. Bounding the width makes overflow-x-auto scroll
        in place instead. 28rem = drawer max-w-md; 3rem = drawer p-4 + card p-2.
      */}
      <div
        ref={scrollRef}
        className="mt-2 flex max-w-[calc(min(100vw,28rem)_-_3rem)] gap-2 overflow-x-auto scroll-smooth pb-1"
      >
        {model.days.map((day) => (
          <DayCard key={day.dayKey} day={day} />
        ))}
      </div>

      {model.booked && (
        <div className="mt-1.5 font-mono text-xs text-foreground">
          <span className="text-brand-blue">✓</span> Booked{" "}
          <span className="font-semibold">
            {formatSlotRange(model.booked.start, model.booked.end)}
          </span>
        </div>
      )}
    </div>
  );
}

function ScrollButton({ dir, onClick }: { dir: 1 | -1; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 1 ? "next day" : "previous day"}
      className="rounded border border-border bg-brand-blue p-0.5 text-white shadow-sm transition hover:brightness-110 active:translate-y-px"
    >
      {dir === 1 ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
    </button>
  );
}

function DayCard({ day }: { day: DayTimeline }) {
  const span = Math.max(1, day.axisEndMs - day.axisStartMs);
  const pct = (ms: number) => ((ms - day.axisStartMs) / span) * 100;
  const bar = (iv: Interval) => ({
    left: `${pct(iv.startMs)}%`,
    width: `${Math.max(2, ((iv.endMs - iv.startMs) / span) * 100)}%`,
  });

  return (
    <div className="w-[220px] shrink-0 rounded-md border border-border bg-secondary p-1.5">
      <div className="font-comic-headline text-xs font-semibold text-foreground">
        {DAY_FMT.format(new Date(day.axisStartMs))}
      </div>

      <div className="mt-1.5 space-y-1">
        {day.lanes.map((lane) => (
          <Lane key={lane.agentId} label={lane.label}>
            {lane.ranges.map((iv, i) => (
              <div
                key={i}
                title={formatSlotRange(iv.start, iv.end)}
                className="absolute top-0.5 bottom-0.5 rounded-sm border border-brand-blue bg-brand-blue/20"
                style={bar(iv)}
              />
            ))}
          </Lane>
        ))}

        {/* Overlap band — the times everyone shares this day. */}
        <Lane label="overlap" emphasize>
          {day.overlap.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-mono text-[8px] text-muted-foreground">no overlap</span>
            </div>
          )}
          {day.overlap.map((iv, i) => (
            <div
              key={i}
              title={formatSlotRange(iv.start, iv.end)}
              className="absolute top-0.5 bottom-0.5 rounded-sm border border-brand-blue bg-brand-blue"
              style={bar(iv)}
            />
          ))}
          {day.booked && (
            <div
              title={`Booked ${formatSlotRange(day.booked.start, day.booked.end)}`}
              className="absolute top-0 bottom-0 rounded-sm border-2 border-foreground bg-foreground"
              style={bar(day.booked)}
            />
          )}
        </Lane>
      </div>

      {/* Axis ticks and the lane labels below are chart furniture, not body
          copy, so they stay below the text-sm floor: this card is only 220px
          wide and a 12px mono label truncates inside its 48px lane. */}
      <div className="mt-1 flex justify-between pl-[3.375rem] font-mono text-[8px] text-muted-foreground">
        <span>{formatTimeOfDay(new Date(day.axisStartMs))}</span>
        <span>{formatTimeOfDay(new Date(day.axisEndMs))}</span>
      </div>
    </div>
  );
}

function Lane({
  label,
  emphasize,
  children,
}: {
  label: string;
  emphasize?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      <div
        className={cn(
          "w-12 shrink-0 truncate font-mono text-[8px]",
          emphasize ? "font-semibold text-brand-blue" : "text-muted-foreground",
        )}
        title={label}
      >
        {label}
      </div>
      <div
        className={cn(
          "relative h-4 flex-1 rounded-sm border border-border",
          emphasize ? "bg-accent" : "bg-muted",
        )}
      >
        {children}
      </div>
    </div>
  );
}
